import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./WbsPage.css";
import { apiGet, apiPost, apiPut, createJobPoller, forgetRememberedJob, readRememberedJob, rememberJob } from "../apiClient";
import {
  ACTIVE_JOB_STATUSES,
  VIEW_SCOPE_WORKSPACE,
  WBS_SOURCE_IMAGE,
  WBS_SOURCE_MANUAL,
  WBS_SOURCE_REQUIREMENTS,
  WBS_SOURCE_TASKS
} from "../domainCatalog";
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  PROJECT_HEIGHT,
  PROJECT_WIDTH,
  buildWbsLayout,
  drawCenteredWrappedText,
  drawWrappedText,
  isDescendant,
  jobStatusMessage,
  normalizeAnalyzedWbs,
  normalizeNodes,
  readAsDataUrl,
  reorderWbsNode,
  roundRect,
  serializeWbs,
  slugify
} from "./wbsStudio/wbsUtils";

export { buildWbsLayout, reorderWbsNode } from "./wbsStudio/wbsUtils";

const WBS_GENERATION_JOB_KEY = "taskit.wbsGenerationJob";

const WbsPage = ({ token, model, scope = "workstream", onUsageChange }) => {
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState("Work Breakdown Structure");
  const [nodes, setNodes] = useState([]);
  const [loadingAction, setLoadingAction] = useState(null);
  const [message, setMessage] = useState("");
  const [draggedId, setDraggedId] = useState(null);
  const [sourceType, setSourceType] = useState(WBS_SOURCE_MANUAL);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [requirementsChanged, setRequirementsChanged] = useState(false);
  const [generationJob, setGenerationJob] = useState(null);

  const layout = useMemo(() => buildWbsLayout(nodes), [nodes]);
  const loading = Boolean(loadingAction);
  const currentSnapshot = serializeWbs(title, nodes);
  const hasUnsavedChanges = savedSnapshot !== null
    ? currentSnapshot !== savedSnapshot
    : nodes.length > 0;
  const workspaceView = scope === VIEW_SCOPE_WORKSPACE;
  const activeGenerationJobId = generationJob && ACTIVE_JOB_STATUSES.includes(generationJob.status)
    ? generationJob.id
    : null;

  const loadSavedWbs = useCallback(async () => {
    setLoadingAction("load");
    try {
      const data = await apiGet(`/wbs${scope === VIEW_SCOPE_WORKSPACE ? "?scope=workspace" : ""}`, { token });
      if (!data) {
        setSavedSnapshot(serializeWbs("Work Breakdown Structure", []));
        return;
      }

      const savedTitle = data.title || "Work Breakdown Structure";
      const savedNodes = normalizeNodes(data.nodes || []);
      setTitle(savedTitle);
      setNodes(savedNodes);
      setSourceType(data.source_type || WBS_SOURCE_MANUAL);
      setSavedSnapshot(serializeWbs(savedTitle, savedNodes));
      setRequirementsChanged(Boolean(data.requirements_changed));
      setMessage("Latest saved WBS loaded.");
    } catch {
      setMessage("Could not load the latest saved WBS.");
    } finally {
      setLoadingAction(null);
    }
  }, [token, scope]);

  useEffect(() => {
    loadSavedWbs();
  }, [loadSavedWbs]);

  useEffect(() => {
    const remembered = readRememberedJob(WBS_GENERATION_JOB_KEY);
    if (!remembered?.id) return;
    setGenerationJob(remembered);
    setLoadingAction(remembered.source || WBS_SOURCE_REQUIREMENTS);
    setMessage("Resuming WBS generation job...");
  }, []);

  const applyGeneratedWbs = useCallback((data, source) => {
    const normalized = normalizeAnalyzedWbs(data.title, data.nodes);
    setTitle(normalized.title);
    setNodes(normalized.nodes);
    setSourceType(source);
    setRequirementsChanged(false);
    setMessage("WBS draft ready. Review it and click Save latest version.");
    onUsageChange?.();
  }, [onUsageChange]);

  const loadGenerationJob = useCallback(async (jobId) => {
    if (!jobId) return null;
    try {
      const data = await apiGet(`/jobs/${jobId}`, { token });
      const source = generationJob?.source;
      setGenerationJob({ ...data, source });
      setMessage(jobStatusMessage(data, "WBS generation"));
      if (!ACTIVE_JOB_STATUSES.includes(data.status)) {
        forgetRememberedJob(WBS_GENERATION_JOB_KEY);
        setLoadingAction(null);
        if (data.status === "completed") {
          applyGeneratedWbs(data.result_payload, source);
        } else if (data.status === "failed") {
          setMessage(data.error_message || "WBS generation failed.");
        }
      }
      return data;
    } catch (error) {
      forgetRememberedJob(WBS_GENERATION_JOB_KEY);
      setLoadingAction(null);
      setMessage(error.message || "Error loading WBS generation job.");
      return null;
    }
  }, [applyGeneratedWbs, generationJob?.source, token]);

  useEffect(() => {
    if (!activeGenerationJobId) return undefined;
    return createJobPoller(
      () => loadGenerationJob(activeGenerationJobId),
      { shouldContinue: (job) => Boolean(job && ACTIVE_JOB_STATUSES.includes(job.status)), immediate: true }
    );
  }, [activeGenerationJobId, loadGenerationJob]);

  const analyzeFile = async (file) => {
    if (!file) return;
    if (file.type !== "image/png") {
      setMessage("Please select a PNG image.");
      return;
    }

    setLoadingAction(WBS_SOURCE_IMAGE);
    setMessage("Reading the WBS hierarchy...");

    try {
      const imageDataUrl = await readAsDataUrl(file);
      const job = await apiPost("/wbs/analyze", { image_data_url: imageDataUrl }, { token, model });
      setGenerationJob({ ...job, source: WBS_SOURCE_IMAGE });
      rememberJob(WBS_GENERATION_JOB_KEY, { id: job.id, source: WBS_SOURCE_IMAGE, status: job.status });
      setMessage("WBS image analysis queued.");
    } catch (error) {
      setMessage(error.message || "Error calling API.");
      setLoadingAction(null);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const generateFromRequirements = async () => {
    setLoadingAction(WBS_SOURCE_REQUIREMENTS);
    setMessage("Creating a WBS from the latest saved requirements...");

    try {
      const job = await apiPost("/wbs/generate-from-requirements", undefined, { token, model });
      setGenerationJob({ ...job, source: WBS_SOURCE_REQUIREMENTS });
      rememberJob(WBS_GENERATION_JOB_KEY, { id: job.id, source: WBS_SOURCE_REQUIREMENTS, status: job.status });
      setMessage("WBS generation queued.");
    } catch (error) {
      setMessage(error.message || "Error calling API.");
      setLoadingAction(null);
    }
  };

  const generateFromTasks = async () => {
    setLoadingAction(WBS_SOURCE_TASKS);
    setMessage("Updating the WBS from the latest Task Studio backlog...");

    try {
      const data = await apiPost("/wbs/generate-from-tasks", undefined, { token });

      applyGeneratedWbs(data, WBS_SOURCE_TASKS);
    } catch (error) {
      setMessage(error.message || "Error calling API.");
    } finally {
      setLoadingAction(null);
    }
  };

  const updateTitle = (id, value) => {
    setNodes((current) => current.map((node) => (
      node.id === id ? { ...node, title: value } : node
    )));
  };

  const addNode = (parentId = null) => {
    const id = `item-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setNodes((current) => [...current, { id, parent_id: parentId, title: "New work item" }]);
    if (!nodes.length) setSourceType(WBS_SOURCE_MANUAL);
  };

  const deleteNode = (id) => {
    setNodes((current) => {
      const removed = new Set([id]);
      const pending = [id];
      while (pending.length) {
        const parentId = pending.shift();
        for (const node of current) {
          if (node.parent_id === parentId && !removed.has(node.id)) {
            removed.add(node.id);
            pending.push(node.id);
          }
        }
      }
      return current.filter((node) => !removed.has(node.id));
    });
  };

  const moveNode = (targetId, event) => {
    if (!draggedId || draggedId === targetId || isDescendant(nodes, targetId, draggedId)) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeY = (event.clientY - bounds.top) / bounds.height;
    const placement = relativeY < 0.3 ? "before" : relativeY > 0.7 ? "after" : "child";
    setNodes((current) => reorderWbsNode(current, draggedId, targetId, placement));
    setMessage(
      placement === "child"
        ? "Item moved under its new parent. Save the WBS to keep the change."
        : "WBS order changed. Save the WBS to keep the change."
    );
    setDraggedId(null);
  };

  const moveToRoot = () => {
    if (!draggedId) return;
    setNodes((current) => current.map((node) => (
      node.id === draggedId ? { ...node, parent_id: null } : node
    )).filter((node) => node.id !== draggedId).concat(
      current.filter((node) => node.id === draggedId).map((node) => ({ ...node, parent_id: null }))
    ));
    setMessage("Epic moved to the end of the horizontal row. Save the WBS to keep the change.");
    setDraggedId(null);
  };

  const saveWbs = async () => {
    if (!nodes.length || !title.trim()) return;
    setLoadingAction("save");
    setMessage("Saving the latest WBS version...");

    try {
      const data = await apiPut("/wbs", {
        title,
        nodes,
        source_type: sourceType
      }, { token });

      const savedTitle = data.title || title;
      const savedNodes = normalizeNodes(data.nodes || nodes);
      setTitle(savedTitle);
      setNodes(savedNodes);
      setSourceType(data.source_type);
      setSavedSnapshot(serializeWbs(savedTitle, savedNodes));
      setRequirementsChanged(Boolean(data.requirements_changed));
      setMessage("Latest WBS version saved.");
    } catch (error) {
      setMessage(error.message || "Error saving the WBS.");
    } finally {
      setLoadingAction(null);
    }
  };

  const exportPng = () => {
    if (!nodes.length) return;
    const exportLayout = buildWbsLayout(nodes);
    const canvas = document.createElement("canvas");
    canvas.width = exportLayout.width;
    canvas.height = exportLayout.height;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = "#0f2857";
    ctx.lineWidth = 2;
    exportLayout.connectorPaths.forEach((points) => {
      ctx.beginPath();
      points.forEach((point, index) => {
        if (index === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.stroke();
    });

    roundRect(ctx, exportLayout.project.x, exportLayout.project.y, PROJECT_WIDTH, PROJECT_HEIGHT, 12);
    ctx.fillStyle = "#061a46";
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 24px Arial";
    drawCenteredWrappedText(ctx, title, exportLayout.project.x, exportLayout.project.y + 35, PROJECT_WIDTH, 28);

    exportLayout.items.forEach(({ node, x, y, kind, depth }) => {
      roundRect(ctx, x, y, NODE_WIDTH, NODE_HEIGHT, 8);
      ctx.fillStyle = kind === "epic" ? "#edf7eb" : "#ffffff";
      ctx.fill();
      ctx.strokeStyle = kind === "epic" ? "#7ac99a" : "#b9d8c6";
      ctx.stroke();
      ctx.fillStyle = "#0f172a";
      ctx.font = "bold 14px Arial";
      drawWrappedText(ctx, node.title, x + 12 + depth * 8, y + 26, NODE_WIDTH - 24 - depth * 8, 18);
    });

    const link = document.createElement("a");
    link.download = `${slugify(title) || "wbs"}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div className="wbs-page">
      <div className="wbs-header">
        <div>
          <h1>WBS Studio</h1>
          <p>{workspaceView ? "Consolidated WBS from all workstreams." : "Generate from requirements, update from Task Studio, or upload an existing WBS PNG."}</p>
        </div>
        <div className="wbs-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png"
            hidden
            onChange={(event) => analyzeFile(event.target.files?.[0])}
          />
          <button onClick={() => fileInputRef.current?.click()} disabled={loading || workspaceView}>
            {loadingAction === WBS_SOURCE_IMAGE ? "Analyzing PNG..." : "Upload PNG"}
          </button>
          <button className="requirements" onClick={generateFromRequirements} disabled={loading || workspaceView}>
            {loadingAction === WBS_SOURCE_REQUIREMENTS ? "Generating WBS..." : "Generate from latest requirements"}
          </button>
          <button className="tasks" onClick={generateFromTasks} disabled={loading || workspaceView}>
            {loadingAction === WBS_SOURCE_TASKS ? "Updating WBS..." : "Update from Task Studio"}
          </button>
          <button onClick={() => addNode(null)} disabled={loading || workspaceView}>Add epic</button>
          <button
            className="save"
            onClick={saveWbs}
            disabled={loading || workspaceView || !nodes.length || !hasUnsavedChanges}
          >
            {loadingAction === "save" ? "Saving..." : "Save latest version"}
          </button>
          <button className="primary" onClick={exportPng} disabled={!nodes.length}>Export landscape PNG</button>
        </div>
      </div>

      <div className={`wbs-status ${hasUnsavedChanges ? "unsaved" : ""}`}>
        <strong>{nodes.length ? title : "No WBS saved"}</strong>
        <span>
          {hasUnsavedChanges
            ? "Unsaved draft"
            : nodes.length
              ? "Latest version saved"
              : "Generate, upload, or create a WBS"}
        </span>
      </div>

      {requirementsChanged && (
        <div className="wbs-requirements-warning">
          Requirements changed after this WBS was saved. Regenerate when you are ready to review a new draft.
        </div>
      )}

      {message && <div className="wbs-message">{message}</div>}

      {!nodes.length && !loading ? (
        <div className="wbs-empty-state">
          <button className="wbs-dropzone requirements" onClick={generateFromRequirements} disabled={workspaceView}>
            <strong>Generate from latest requirements</strong>
            <span>The AI will create editable epics and tasks from the document saved in Project Context.</span>
          </button>
          <button className="wbs-dropzone tasks" onClick={generateFromTasks} disabled={workspaceView}>
            <strong>Create from Task Studio</strong>
            <span>Stories and sub-tasks from the latest backlog will become an editable WBS.</span>
          </button>
          <button className="wbs-dropzone" onClick={() => fileInputRef.current?.click()} disabled={workspaceView}>
            <strong>Upload a WBS PNG</strong>
            <span>The AI will turn the diagram into editable work items.</span>
          </button>
        </div>
      ) : (
        <>
          <div
            className={draggedId ? "wbs-root-drop active" : "wbs-root-drop"}
            onDragOver={(event) => event.preventDefault()}
            onDrop={moveToRoot}
          >
            Drop here to move an item to the end of the epic row
          </div>
          <div className="wbs-canvas-shell">
            <div className="wbs-canvas" style={{ width: layout.width, height: layout.height }}>
              <svg className="wbs-lines" width={layout.width} height={layout.height}>
                {layout.connectorPaths.map((points, index) => (
                  <polyline key={index} points={points.map((point) => `${point.x},${point.y}`).join(" ")} />
                ))}
              </svg>
              <div
                className="wbs-project-node"
                style={{ left: layout.project.x, top: layout.project.y }}
              >
                <textarea
                  value={title}
                  onChange={(event) => !workspaceView && setTitle(event.target.value)}
                  aria-label="WBS title"
                />
                <button onClick={() => addNode(null)} disabled={workspaceView}>+ Story</button>
              </div>
              {layout.items.map(({ node, x, y, kind, depth }) => (
                <div
                  className={`wbs-node ${kind}`}
                  key={node.id}
                  style={{ left: x, top: y }}
                  draggable={!workspaceView}
                  onDragStart={() => setDraggedId(node.id)}
                  onDragEnd={() => setDraggedId(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    moveNode(node.id, event);
                  }}
                >
                  <input
                    style={{ paddingLeft: depth ? `${depth * 6}px` : undefined }}
                    value={node.title}
                    onChange={(event) => !workspaceView && updateTitle(node.id, event.target.value)}
                  />
                  <div className="wbs-node-actions">
                    <button onClick={() => addNode(node.id)} disabled={workspaceView}>+ Child</button>
                    <button onClick={() => deleteNode(node.id)} disabled={workspaceView}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WbsPage;
